import { FC } from "react";
import { AuthBrandLogo } from "components/AuthBrandLogo";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useResetPassword } from "hooks/useAuth";
import styled from "@emotion/styled";
import { Button, Col, Form, Input, message, Row, Typography } from "antd";
import { EyeInvisibleOutlined, EyeOutlined } from "@ant-design/icons";
import { encrypt } from "utils/encryption";
import { loginEncryptionKey } from "config";

const ResetPasswordPage: FC = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const navigate = useNavigate();
  const [form] = Form.useForm();

  const { mutate: submitResetPassword, isPending } = useResetPassword();

  const handleSubmit = (values: { password: string; confirmPassword: string }) => {
    if (!token) return;

    // const encryptedPassword = loginEncryptionKey
    //   ? encrypt(values.password, loginEncryptionKey)
    //   : values.password;

    submitResetPassword(
      { reset_token: token, new_password: values.password },
      {
        onSuccess: (data) => {
          if (data.status === "success") {
            message.success("เปลี่ยนรหัสผ่านสำเร็จ กรุณาเข้าสู่ระบบใหม่");
            navigate("/sign-in");
          } else {
            message.error(data.error_message || "เกิดข้อผิดพลาด กรุณาลองใหม่อีกครั้ง");
          }
        },
        onError: () => {
          message.error("เกิดข้อผิดพลาด กรุณาลองใหม่อีกครั้ง");
        },
      }
    );
  };

  return (
    <div style={{ minHeight: "100vh", backgroundColor: "#fff" }}>
      <Row gutter={[0, 0]}>
        <Col style={{ padding: 0 }} xs={{ span: 0 }} lg={{ span: 16 }} md={{ span: 16 }}>
          <BackgroundGradient>
            <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100vh" }}>
              <AuthBrandLogo />
            </div>
          </BackgroundGradient>
        </Col>
        <Col
          xs={{ span: 24, offset: 0 }}
          lg={{ span: 8, offset: 0 }}
          md={{ span: 8 }}
          style={{ display: "flex", flexDirection: "column", justifyContent: "center", height: "100vh" }}
        >
          <SectionText>
            <Typography.Title level={4}>ตั้งรหัสผ่านใหม่</Typography.Title>
          </SectionText>

          <SectionInput style={{ marginTop: 20 }}>
            {!token ? (
              <div style={{ textAlign: "center" }}>
                <Typography.Text type="danger">
                  ลิงก์ไม่ถูกต้องหรือหมดอายุแล้ว
                </Typography.Text>
                <div style={{ marginTop: 16 }}>
                  <Link to="/forgot-password">ขอลิงก์รีเซ็ตรหัสผ่านใหม่</Link>
                </div>
              </div>
            ) : (
              <Form
                onFinish={handleSubmit}
                layout="vertical"
                form={form}
                style={{ display: "flex", flexDirection: "column", gap: 8 }}
              >
                <Form.Item
                  name="password"
                  rules={[
                    { required: true, message: "กรุณากรอกรหัสผ่านใหม่" },
                    { min: 8, message: "รหัสผ่านต้องมีอย่างน้อย 8 ตัวอักษร" },
                  ]}
                >
                  <Input.Password
                    placeholder="รหัสผ่านใหม่"
                    autoComplete="new-password"
                    iconRender={(visible) => (visible ? <EyeOutlined /> : <EyeInvisibleOutlined />)}
                  />
                </Form.Item>

                <Form.Item
                  name="confirmPassword"
                  dependencies={["password"]}
                  rules={[
                    { required: true, message: "กรุณายืนยันรหัสผ่าน" },
                    ({ getFieldValue }) => ({
                      validator(_, value) {
                        if (!value || getFieldValue("password") === value) {
                          return Promise.resolve();
                        }
                        return Promise.reject(new Error("รหัสผ่านไม่ตรงกัน"));
                      },
                    }),
                  ]}
                >
                  <Input.Password
                    placeholder="ยืนยันรหัสผ่าน"
                    autoComplete="new-password"
                    iconRender={(visible) => (visible ? <EyeOutlined /> : <EyeInvisibleOutlined />)}
                  />
                </Form.Item>

                <Form.Item>
                  <Button type="primary" htmlType="submit" style={{ width: "100%" }} loading={isPending} size="large">
                    ยืนยันการเปลี่ยนรหัสผ่าน
                  </Button>
                </Form.Item>
              </Form>
            )}

            <div style={{ textAlign: "center", marginTop: 16 }}>
              <Link to="/sign-in">กลับหน้าเข้าสู่ระบบ</Link>
            </div>
          </SectionInput>
        </Col>
      </Row>
    </div>
  );
};

export default ResetPasswordPage;

const SectionText = styled.div`
  width: 100%;
  margin-top: 10px;
  text-align: center;
  padding: 0 5px;
`;

const SectionInput = styled.div`
  padding: 0 20px;
`;

const BackgroundGradient = styled.div`
  background: linear-gradient(45deg, #2777c8, #fff);
  width: 100%;
  height: 100vh;
`;
